// newsPodcast — a single, shared, wall-clock-synced feed of the platform's News
// podcast, for the flat "TV in a window" screens around the world.
//
// The News podcast is a podcast EPISODE whose `visual_timeline` is an ordered
// cue sheet ({ startTime (s), mediaUrl }) force-aligned to the narration on the
// web /listen page (PodcastHeroStage). Here we render those same slides on world
// TVs — but SYNCED across every screen (and every client) by driving the cue
// index off the wall clock modulo the episode duration, the same time-offset
// trick the radio stations use. No per-TV audio: these are ambient muted
// screens, so there's no autoplay gate and nothing to clash with the station.
//
// One module-level load feeds all TVs, so they're identical by construction.
import { API_BASE } from '../config';
import { audioEngine } from './audioEngine';

// GCS images are CORS-gated for THREE.TextureLoader; route them through the API
// asset proxy (same approach as Interior.jsx proxiedMedia).
export function proxyMedia(url) {
  try {
    const u = new URL(url);
    if (u.hostname === 'storage.googleapis.com') {
      const parts = u.pathname.replace(/^\//, '').split('/');
      const bucket = parts.shift();
      const name = parts.map(decodeURIComponent).join('/');
      return `${API_BASE}/api/assets/file?bucket=${encodeURIComponent(bucket)}&name=${encodeURIComponent(name)}`;
    }
  } catch { /* passthrough */ }
  return url;
}

let _promise = null;   // in-flight / resolved load (shared)
let _feed = null;      // { cues:[{t, url}], duration, title, show } | null

function pickNewsEpisode(episodes) {
  if (!Array.isArray(episodes) || !episodes.length) return null;
  // Prefer a news-format show; fall back to the newest episode of any kind.
  const isNews = (e) => /news/i.test(e.show_format || '') || /news/i.test(e.show_name || '') || /news/i.test(e.title || '');
  return episodes.find(isNews) || episodes[0];
}

async function _load() {
  const listRes = await fetch(`${API_BASE}/api/podcast/episodes?limit=20`, { cache: 'no-store' });
  const list = await listRes.json();
  const pick = pickNewsEpisode(list?.episodes);
  if (!pick) throw new Error('no episodes');

  const epRes = await fetch(`${API_BASE}/api/podcast/episodes/${pick.episode_id}`, { cache: 'no-store' });
  // The single-episode endpoint wraps the object: { episode: {...} }.
  const ep = (await epRes.json())?.episode || {};
  const rawCues = Array.isArray(ep?.visual_timeline?.cues) ? ep.visual_timeline.cues : [];
  const cues = rawCues
    .filter((c) => c && c.mediaUrl)
    .map((c) => ({ t: Number(c.startTime) || 0, url: proxyMedia(c.mediaUrl) }))
    .sort((a, b) => a.t - b.t);

  // Cover as a lone fallback slide if the episode has no timeline yet.
  if (cues.length === 0 && (ep?.cover_image_url || pick.cover_image_url)) {
    cues.push({ t: 0, url: proxyMedia(ep.cover_image_url || pick.cover_image_url) });
  }
  if (cues.length === 0) throw new Error('no slides');

  const lastT = cues[cues.length - 1].t;
  const duration = Math.max(Number(ep?.duration_seconds) || 0, lastT + 6);
  // Audio plays through a plain <audio> element — use the RAW signed GCS URL
  // (no proxy): cross-origin playback needs no CORS, and GCS supports range
  // requests so seeking (sync-to-clock) works. (Only the IMAGES need proxying,
  // because THREE.TextureLoader samples pixels and IS CORS-gated.)
  const audioUrl = ep?.audio_url || null;
  _feed = { cues, duration, audioUrl, title: ep?.title || pick.title || 'The News', show: ep?.show_name || pick.show_name || 'News' };
  return _feed;
}

// Kick off (or reuse) the shared load. Safe to call from many TVs.
export function loadNews() {
  if (!_promise) _promise = _load().catch((e) => { _promise = null; throw e; });
  return _promise;
}

// The cue index every TV should be showing right now, from the shared wall clock
// (all clients agree). Returns -1 before the feed is ready.
export function currentIndex(now = Date.now()) {
  if (!_feed) return -1;
  const t = (now / 1000) % _feed.duration;
  let idx = 0;
  for (let i = 0; i < _feed.cues.length; i++) {
    if (t >= _feed.cues[i].t) idx = i; else break;
  }
  return idx;
}

export function feed() { return _feed; }

// ── Hover-to-listen (barks-style) ────────────────────────────────────────────
// The TVs are silent by default; rolling over one plays the news NARRATION,
// synced to the same shared wall clock as the slideshow, and rolling off mutes
// it. One shared <audio> element serves every TV (they're the same stream), and
// a hover COUNT means moving between adjacent TVs never cuts the audio. The
// district station dips underneath while it plays, like the ambient barks.
let _audio = null;
let _hoverCount = 0;

function ensureAudio() {
  if (_audio || !_feed?.audioUrl) return _audio;
  _audio = new Audio();
  _audio.preload = 'auto';
  _audio.loop = true;
  // NO crossOrigin: the signed GCS URL doesn't return Access-Control-Allow-Origin,
  // so a CORS request would be blocked and the audio would never play (the same
  // gotcha the station audio element documents). Plain playback needs no CORS.
  _audio.src = _feed.audioUrl;
  return _audio;
}

// Snap playback to the moment the slideshow is showing, so audio + slides agree.
function syncToClock() {
  if (!_audio || !_feed) return;
  const t = (Date.now() / 1000) % _feed.duration;
  try { if (Math.abs((_audio.currentTime || 0) - t) > 0.5) _audio.currentTime = t; } catch { /* not seekable yet */ }
}

export function newsHoverEnter() {
  const a = ensureAudio();
  if (!a) return;
  _hoverCount += 1;
  syncToClock();
  // The mixer takes the news to the top of the mix — silencing the cinema movie
  // and the station underneath — then plays it. Re-sync once it's rolling.
  audioEngine.pushOverride(a);
  a.play?.().then(syncToClock).catch(() => {});
}

export function newsHoverLeave() {
  _hoverCount = Math.max(0, _hoverCount - 1);
  if (_hoverCount === 0 && _audio) {
    audioEngine.popOverride(_audio); // hands the mix back to the movie / station
  }
}
